import { useRef } from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { Arrow, ArrowDirection } from './combined-navigation';
import type { NavigationProps } from './pagination/types';
import { useSlider } from './SliderContext';

export const Navigation = ({
  sliderId,
  prevSlide,
  showPrev,
  showNext,
  nextSlide,
  navigationClassName,
  colors: {
    color,
    background,
    hover: { color: colorHover, background: backgroundHover }
  },
  customIcons: { prev, next }
}: NavigationProps) => {
  const { appClassnamePrefix } = useSlider();
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);

  return (
    <StyledNavigation
      _appClassPrefix={appClassnamePrefix}
      _arrowBackgroundColor={background}
      _arrowHoverBackgroundColor={backgroundHover}
      _iconFillColor={color}
      _iconHoverFillColor={colorHover}
      _navigationClass={navigationClassName}
      aria-label="Slider Controls"
    >
      <Arrow
        elementRef={prevRef}
        oppositeRef={nextRef}
        sliderId={sliderId}
        navigationClassName={navigationClassName}
        customIcon={prev}
        direction={ArrowDirection.Prev}
        onClick={() => prevSlide()}
        isEnabled={showPrev()}
      />
      <Arrow
        elementRef={nextRef}
        oppositeRef={prevRef}
        sliderId={sliderId}
        navigationClassName={navigationClassName}
        customIcon={next}
        direction={ArrowDirection.Next}
        onClick={() => nextSlide()}
        isEnabled={showNext()}
      />
    </StyledNavigation>
  );
};

interface StyledNavigationProps {
  _appClassPrefix: string;
  _navigationClass: string;
  _arrowBackgroundColor: string;
  _arrowHoverBackgroundColor: string;
  _iconFillColor: string;
  _iconHoverFillColor: string;
}

const StyledNavigation = styled.div.attrs<StyledNavigationProps>(
  ({ _appClassPrefix, _navigationClass }) => ({
    className: classNames(
      `${_appClassPrefix}-navigation-navigation`,
      _navigationClass
    )
  })
)`
  display: flex;
  justify-content: space-between;

  ${({
    _appClassPrefix,
    _arrowBackgroundColor,
    _arrowHoverBackgroundColor,
    _iconFillColor,
    _iconHoverFillColor
  }) => css`
    & .${_appClassPrefix}-navigation-arrow {
      ${_arrowBackgroundColor && `background-color: ${_arrowBackgroundColor};`}
    }

    & .${_appClassPrefix}-navigation-icon {
      ${_iconFillColor && `fill: ${_iconFillColor};`}
    }

    & .${_appClassPrefix}-navigation-arrow:hover {
      ${_arrowHoverBackgroundColor &&
      `background-color: ${_arrowHoverBackgroundColor};`}
    }

    &
      .${_appClassPrefix}-navigation-arrow:hover
      .${_appClassPrefix}-navigation-icon {
      ${_iconHoverFillColor && `fill: ${_iconHoverFillColor};`}
    }
  `}
`;
